import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import {
 Heading,
 Flex,
 Box,
 useToast,
 Accordion,
 AccordionPanel,
 AccordionItem,
 AccordionButton,
 AccordionIcon,
 Center,
 Text,
 Progress,
 Button,
} from '@chakra-ui/react';
import { QuestionCardReviewer, QuestionCardSimple, PillarSelectorRow } from '../../components/Card';
import baseService from '../../services/base';
import { useSSO } from '../../context/SSO';
import { AttachEvidenceModal } from '../../components/Modals';
import { downloadFile } from '../../helpers/downloadFile';
import { DownloadResponsesButton, EvidenceDownloadButton } from '../../components/Button';
import RespondentViewThemes from '../../components/Survey/RespondentViewThemes';
import { useResponsesDownload } from '../../hooks/useResponsesDownload';
import useAuditHeaderData, { useIsAudit } from '../audit/useAuditHeaderData';

async function fetch(url) {
 const response = await baseService.get(url);
 if (!response?.data || Object.keys(response?.data)?.length === 0) {
  throw new Error('Empty response data');
 }
 return response.data;
}

export default function DiagnosisType({
 companyId,
 surveyId,
 companyGroupId,
 formName,
 selectedCompanyId,
 isResponsibleUser = false,
 mutate,
 openChat,
}) {
 const { user } = useSSO();
 const toast = useToast();
 const isAudit = useIsAudit();
 const { downloadResponses, isLoadingResponses: isDownloadingResponses } =
  useResponsesDownload();

 const [isEvidenceModalOpen, setEvidenceModalOpen] = useState(false);
 const [currentQuestionId, setCurrentQuestionId] = useState();
 const [currentFiles, setCurrentFiles] = useState([]);
 const [selectedPillar, setSelectedPillar] = useState();
 const [isLoadingEvidence, setLoadingEvidence] = useState(false);
 const [themeIndex, setThemeIndex] = useState(0);
 const [isLoadingResponses, setLoadingResponses] = useState(false);
 const [isViewMode, setIsViewMode] = useState(false);

 const { data, isLoading } = useSWR(
  surveyId && companyId ? `survey/diagnostic/${surveyId}?id_company=${companyId}` : null,
  fetch
 );

 const { data: currentThemesData } = useSWR(
  selectedPillar
   ? `survey/admin/pillar/answered?id_company=${companyId}&id_pillar=${selectedPillar}&id_survey=${surveyId}`
   : null,
  fetch
 );

 useEffect(() => {
  if (data?.pillars?.length > 0) {
   let idPillar = data.pillars[0].id_pillar;
   let indexTheme = 0;
   if (!selectedPillar) {
    if (openChat && openChat.themeId) {
     data.pillars.forEach((p) => {
      let exist = p.themes.filter((theme) => theme.id_theme === openChat.themeId);
      if (exist !== undefined && exist.length > 0) {
       idPillar = p.id_pillar;
       indexTheme = p.themes.findIndex((theme) => theme.id_theme === openChat.themeId);
      }
     });
    }
    setThemeIndex(indexTheme);
    setSelectedPillar(idPillar);
   }
  }
 }, [data, selectedPillar]);

 const pillarOptions = useMemo(() => {
  if (!data?.pillars?.length) return [];

  return data?.pillars?.map((pillar) => ({
   label: pillar?.name_pillar,
   value: pillar?.id_pillar,
  }));
 }, [data]);

 const normalizedPillars = useMemo(() => {
  if (!data?.pillars?.length) return [];
  return data.pillars.map((pillar) => {
   const themes = pillar?.themes || [];
   return {
    id_pillar: pillar.id_pillar,
    name_pillar: pillar.name_pillar,
    img_pillar: pillar.img_pillar,
    totalQuestions: themes.reduce((sum, theme) => sum + (theme?.totalQuestions || 0), 0),
    answeredQuestions: themes.reduce((sum, theme) => sum + (theme?.answeredQuestions || 0), 0),
   };
  });
 }, [data]);

 const themeProgress = useMemo(() => {
  const progress = {};
  currentThemesData?.forEach((theme) => {
   const questions = theme?.themes?.flatMap((nivel) => nivel?.questions || []) || [];
   progress[theme?.id_theme] = {
    totalQuestions: questions.length,
    answeredQuestions: questions.filter((question) =>
     question?.users_responses?.some(
      (response) => response?.company?.id_company === +selectedCompanyId
     )
    ).length,
   };
  });
  return progress;
 }, [currentThemesData, selectedCompanyId]);

 const selectedPillarData = useMemo(() => {
  return data?.pillars?.find((pillar) => pillar?.id_pillar === selectedPillar);
 }, [selectedPillar, data]);

 const totalThemes = useMemo(() => currentThemesData?.length || 0, [currentThemesData]);

 const completedThemes = useMemo(() => {
  return (
   currentThemesData?.filter((theme) =>
    theme?.themes?.every((subTheme) =>
     subTheme?.questions?.every((question) =>
      question?.users_responses?.some(
       (response) => response?.company?.id_company === +selectedCompanyId
      )
     )
    )
   ).length || 0
  );
 }, [currentThemesData, selectedCompanyId]);

 const hasEvidenceFiles = useMemo(() => {
  return currentThemesData?.some((theme) =>
   theme?.themes?.some((subTheme) =>
    subTheme?.questions?.some((question) =>
     question?.users_responses?.some(
      (response) =>
       response?.company?.id_company === +selectedCompanyId &&
       response?.response_file?.length > 0
     )
    )
   )
  );
 }, [currentThemesData, selectedCompanyId]);

 const handleQuestionEvidence = useCallback((questionId, response) => {
  setCurrentQuestionId(questionId);
  setCurrentFiles(response?.response_file);
  setEvidenceModalOpen(true);
 }, []);

 const handleCloseCurrentEvidence = useCallback(() => {
  setEvidenceModalOpen(false);
  setCurrentQuestionId(null);
  setCurrentFiles([]);
 }, []);

 async function evidenceDownload() {
  setLoadingEvidence(true);
  try {
   const response = await baseService.get(
    `/downloads?survey=${surveyId}&group=${companyGroupId}&form=${formName}`,
    { responseType: 'arraybuffer' }
   );

   if (response.headers['content-type'].includes('application/json')) {
    const data = JSON.parse(new TextDecoder().decode(response.data));
    downloadFile(data.url, `${formName}_evidence.zip`);
   } else {
    const blob = new Blob([response.data], { type: 'application/zip' });
    downloadFile(window.URL.createObjectURL(blob), `${formName}_evidence.zip`);
   }

   toast({
    title: 'Download das evidências iniciado com sucesso',
    status: 'success',
   });
  } catch (error) {
   toast({
    title: 'Erro ao fazer o download das evidências',
    description: 'Não foi possível processar os arquivos anexados. Tente novamente.',
    status: 'error',
   });
  } finally {
   setLoadingEvidence(false);
  }
 }

 // EG-63: em auditoria o sub-header some; o AuditHeader único mostra o
 // progresso (por tema concluído — mesma métrica do sub-header original).
 useAuditHeaderData(
  isAudit
   ? {
      answered: completedThemes,
      total: totalThemes,
      downloadingResponses: isDownloadingResponses,
      downloadingEvidence: isLoadingEvidence,
      evidenceDisabled: !hasEvidenceFiles,
      onViewAsRespondent: () => setIsViewMode(true),
      onDownloadResponses: () =>
       downloadResponses({ surveyId, companyId, companyGroupId, formName }),
      onDownloadEvidence: () => evidenceDownload(),
     }
   : null
 );

 if (!pillarOptions?.length || !selectedPillar || isLoading) {
  return null;
 }

 if (isViewMode) {
  return (
   <RespondentViewThemes
    surveyId={surveyId}
    companyId={selectedCompanyId}
    userId={user?.user?.id_user}
    pillar={selectedPillarData}
    pillarIndex={0}
    pillarId={selectedPillarData?.id_pillar}
    onClose={() => setIsViewMode(false)}
   />
  );
 }

 return (
  <Box>
   {!isAudit && (
   <Flex bg='white' w='100%' h='107px' p='24px 32px' borderRadius='12px' mt='24px'>
    <Flex w='full' align='center' justify='space-between' gap='6'>
     <Box flexShrink={0} display='inline-flex' flex='1' maxW='120px'>
      <Heading fontSize='20px'>Pilares</Heading>
     </Box>
     <Flex flex='3' align='center' justifyContent='flex-end' gap='20px'>
      {!isResponsibleUser && (
       <Button
        variant='underline'
        fontWeight={600}
        ml='auto'
        onClick={() => setIsViewMode(true)}
       >
        Visualizar como respondente
       </Button>
      )}

      <Flex w='30.07%' overflowX='hidden' minW='27.07%' maxW='371px'>
       <Center
        bg='rgba(89, 143, 60, 0.1)'
        height='50px'
        borderRadius='12px'
        gap='16px'
        px='16px'
        w='full'
        maxW='370px'
       >
        <Text fontSize='sm' whiteSpace='nowrap'>
         Temas concluídos:
        </Text>

        <Box w='114px'>
         <Progress
          value={(completedThemes / totalThemes) * 100}
          height='10px'
          borderRadius='21px'
          colorScheme='green'
          bg='#D4D4D4'
         />
        </Box>

        <Center px='6px' h='32px' bg='white'>
         <Text color='#598F3C' fontSize='sm' fontWeight='bold'>
          {completedThemes < 10 && '0'}
          {completedThemes}/{totalThemes < 10 && '0'}
          {totalThemes}
         </Text>
        </Center>
       </Center>
      </Flex>

      {!isResponsibleUser && (
       <>
        <Flex gap='5' overflowY='hidden' pos='absolute' right='32px' top='70px'>
         <DownloadResponsesButton
          surveyId={surveyId}
          companyId={selectedCompanyId}
          companyGroupId={companyGroupId}
          formName={formName}
          isLoadingResponses={isLoadingResponses}
          setLoadingResponses={setLoadingResponses}
          height='52px'
         />

         <EvidenceDownloadButton
          isLoading={isLoadingEvidence}
          isDisabled={!hasEvidenceFiles}
          onClick={evidenceDownload}
         />
        </Flex>
       </>
      )}
     </Flex>
    </Flex>
   </Flex>
   )}

   {selectedPillar && (
    <Box mt={isAudit ? 0 : '16px'}>
     <PillarSelectorRow
      pillars={normalizedPillars}
      selectedPillar={selectedPillar}
      onSelect={setSelectedPillar}
     />
    </Box>
   )}

   <Accordion allowMultiple defaultIndex={[themeIndex]} p='0' mt='6'>
    {selectedPillarData &&
     currentThemesData &&
     currentThemesData?.map((currentTheme, index) => (
      <AccordionItem
       key={currentTheme?.id_theme}
       bg='white'
       border='none'
       mb='16px'
       borderRadius='12px'
       overflow='hidden'
      >
       {({ isExpanded }) => {
        const progress = themeProgress[currentTheme?.id_theme] || {
         totalQuestions: 0,
         answeredQuestions: 0,
        };
        const isThemeComplete =
         progress.totalQuestions > 0 && progress.answeredQuestions === progress.totalQuestions;

        return (
        <>
         <AccordionButton
          h='78px'
          borderRadius='0'
          bg='white'
          p='6'
          borderLeft='4px solid'
          borderLeftColor={isThemeComplete ? '#598F3C' : '#D4D4D4'}
          _hover={{
           bg: 'white',
          }}
         >
          <Box
           as='span'
           flex='1'
           textAlign='left'
           color={isExpanded ? '#0073BB' : '#5C6066'}
           fontSize='20px'
           fontWeight='bold'
           whiteSpace='nowrap'
          >
           {index + 1}. {currentTheme?.title}
          </Box>

          {progress.totalQuestions > 0 && (
           <Flex align='center' gap='10px' mr='16px' minW='160px'>
            <Box w='90px'>
             <Progress
              value={(progress.answeredQuestions / progress.totalQuestions) * 100}
              h='6px'
              borderRadius='21px'
              colorScheme='green'
              bg='#D4D4D4'
             />
            </Box>
            <Text fontSize='sm' color='#5C6066' whiteSpace='nowrap'>
             {progress.answeredQuestions}/{progress.totalQuestions}
            </Text>
           </Flex>
          )}

          <AccordionIcon />
         </AccordionButton>

         <AccordionPanel p='6' bg='white'>
          <Text color='black' mb='6'>
           Confira as opções que foram selecionadas e que melhor descrevem as práticas
           atualmente em vigor na empresa avaliada.
          </Text>
          {currentTheme?.themes?.map((theme) => (
           <Box key={`${theme?.id_theme}_${theme?.index}_${theme?.nivel}`} mb='6'>
            <Center bg='#EAEEF0' h='39px' minH='38px' mb={['6px', '16px']}>
             <Text color='#5C6066' fontWeight={700}>
              Nível {theme?.nivel}
             </Text>
            </Center>

            {theme?.questions?.map((question) =>
             isResponsibleUser ? (
              <QuestionCardReviewer
               key={question?.id_question}
               question={question}
               handleQuestionEvidence={handleQuestionEvidence}
               surveyId={surveyId}
               mutate={mutate}
               questionnaireType='diagnosis'
              />
             ) : (
              <QuestionCardSimple
               key={`${question?.id_question}_${index}`}
               question={question}
               selectedCompanyId={selectedCompanyId}
               handleQuestionEvidence={handleQuestionEvidence}
               surveyId={surveyId}
               userId={user?.user?.id_user}
               questionnaireType='diagnosis'
               themeId={currentTheme?.id_theme}
               openChat={openChat && openChat.questionId === question.id_question}
              />
             )
            )}
           </Box>
          ))}
         </AccordionPanel>
        </>
        );
       }}
      </AccordionItem>
     ))}
   </Accordion>

   {currentQuestionId && (
    <AttachEvidenceModal
     isOpen={isEvidenceModalOpen}
     onClose={handleCloseCurrentEvidence}
     questionId={currentQuestionId}
     files={currentFiles}
    />
   )}
  </Box>
 );
}
